import React from 'react';
import { Box, Container, Typography, Card, CardContent, Stack, Avatar, Rating, Button, Grid } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';

export interface Testimonial {
  name: string;
  role: string;
  club: string;
  avatar: string;
  rating: number;
  text: string;
}

export const TESTIMONIALS: Testimonial[] = [
  {
    name: 'Marc T.',
    role: 'Entraîneur U17',
    club: 'Club Régional — Occitanie',
    avatar: 'MT',
    rating: 5,
    text: 'Super outil, j\'utilise TacticaFoot depuis 6 mois pour préparer mes séances. Un gain de temps énorme ! Mes joueurs comprennent instantanément les phases de jeu grâce aux animations.',
  },
  {
    name: 'Philippe L.',
    role: 'Responsable Formation',
    club: 'Académie Football — Île-de-France',
    avatar: 'SL',
    rating: 5,
    text: 'La prise en main est vraiment intuitive. En moins de 2 minutes, j\'ai créé ma première composition et partagé l\'animation avec mon staff. C\'est exactement ce qu\'il nous fallait.',
  },
  {
    name: 'Karim B.',
    role: 'Coach football amateur',
    club: 'District — Auvergne',
    avatar: 'KB',
    rating: 5,
    text: 'L\'export MP4 est bluffant. Je partage mes séquences directement sur le groupe WhatsApp de l\'équipe avant chaque match. Les joueurs arrivent préparés, c\'est un vrai plus.',
  },
  {
    name: 'Jean-François M.',
    role: 'Entraineur',
    club: 'Club National 2',
    avatar: 'JM',
    rating: 5,
    text: 'Le meilleur outil tactique que j\'ai testé. Simple, rapide, efficace. Je le recommande à tous mes collègues entraîneurs. La bibliothèque d\'exercices est une vraie mine d\'or.',
  },
  {
    name: 'Ahmed R.',
    role: 'Éducateur Football',
    club: 'Club Municipal — Nord',
    avatar: 'AR',
    rating: 4,
    text: 'TacticaFoot m\'a complètement changé la préparation de mes entraînements. Fini les schémas sur tableau blanc mal compris. Maintenant tout le monde voit exactement les mouvements.',
  },
  {
    name: 'Nathalie C.',
    role: 'Coach Football Féminin',
    club: 'Club Amateur D1',
    avatar: 'NC',
    rating: 5,
    text: 'Idéal pour les clubs qui veulent structurer leur jeu sans passer des heures sur des logiciels complexes. Accessible sur mobile, c\'est un must-have pour tout entraîneur moderne.',
  },
];

const AVATAR_COLORS = [
  '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#00838f',
];

interface Props {
  preview?: boolean;
}

export const TestimonialsSection: React.FC<Props> = ({ preview = true }) => {
  const navigate = useNavigate();
  const displayed = preview ? TESTIMONIALS.slice(0, 3) : TESTIMONIALS;

  return (
    <Box
      component="section"
      id="temoignages"
      sx={{
        py: { xs: 7, md: 10 },
        px: { xs: 2, md: 4 },
        bgcolor: 'rgba(255,255,255,0.03)',
        borderTop: '1px solid rgba(255,255,255,0.07)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          align="center"
          sx={{
            mb: 1.5,
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            fontWeight: 700,
            color: 'white',
          }}
        >
          Ce que disent nos entraîneurs
        </Typography>
        <Typography
          align="center"
          sx={{
            mb: 6,
            color: 'rgba(255,255,255,0.55)',
            fontSize: '1.05rem',
          }}
        >
          Plus de 3 000 entraîneurs font confiance à TacticaFoot chaque semaine
        </Typography>

        <Grid container spacing={3}>
          {displayed.map((t, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  bgcolor: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 3,
                  transition: 'transform 0.25s ease, border-color 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    borderColor: 'rgba(255,215,0,0.3)',
                  },
                }}
              >
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Rating value={t.rating} readOnly size="small" sx={{ color: '#FFD700' }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', flexGrow: 1, fontStyle: 'italic' }}>
                    "{t.text}"
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
                    <Avatar sx={{ bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length], width: 40, height: 40, fontSize: '0.85rem', fontWeight: 700 }}>
                      {t.avatar}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.9rem', lineHeight: 1.2 }}>
                        {t.name}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}>
                        {t.role} — {t.club}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {preview && (
          <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/temoignages')}
              sx={{
                borderColor: 'rgba(255,215,0,0.5)',
                color: '#FFD700',
                '&:hover': {
                  borderColor: '#FFD700',
                  bgcolor: 'rgba(255,215,0,0.07)',
                },
                px: 4,
                py: 1.2,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
              }}
            >
              Voir tous les témoignages
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};
